/**
 * Super Admin Panel - Vollständige Systemverwaltung
 * Multi-Produkt Control Center
 * Unterstützt: Easyclaim24 (Werkstätten) & Autogutachter24 (Gutachter)
 * Layout: Sidebar Navigation mit Produkt-Switcher
 */

import { useState, useEffect, useMemo } from 'react';
import { getBackendUrl } from '../utils/apiUrl';
import { toast } from 'sonner';
import { 
  Users, Building2, Key, Shield, Search, Plus, Edit, Trash2, 
  CheckCircle, XCircle, Eye, EyeOff, Save, RefreshCw, AlertTriangle,
  Settings, Database, Activity, BarChart3, Clock, User, Mail,
  Square, Paintbrush, Wrench, Star, ChevronDown, LogOut, Home,
  TrendingUp, FileText, Calendar, Filter, MoreVertical, 
  UserCheck, UserX, AlertCircle, Info, ChevronRight, ArrowUpRight,
  Layers, Box, Globe, Lock, Unlock, Power, PowerOff, Menu, X,
  Car, ClipboardCheck, Briefcase, Building, Monitor,
  BookOpen, Headphones, MessageSquare, Lightbulb, Accessibility
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// ===== MULTI-PRODUKT KONFIGURATION =====
const PRODUCTS = {
  all: {
    id: 'all',
    name: 'Alle Produkte',
    shortName: 'Gesamt',
    icon: Monitor,
    color: 'bg-gradient-to-r from-blue-600 to-purple-600',
    description: 'Übersicht über alle Plattformen',
    database: null, // Aggregiert alle
    available: true
  },
  easyclaim: {
    id: 'easyclaim',
    name: 'Easyclaim24',
    shortName: 'Easyclaim',
    icon: Car,
    color: 'bg-blue-600',
    description: 'Schadenmanagement für Werkstätten',
    database: 'intelilack', // Aktuelle DB
    userTypes: ['Werkstätten', 'Autoglas', 'Karosserie'],
    available: true
  },
  autogutachter: {
    id: 'autogutachter',
    name: 'Autogutachter24',
    shortName: 'Gutachter',
    icon: ClipboardCheck,
    color: 'bg-purple-600',
    description: 'Gutachtenerstellung für Sachverständige',
    database: 'autogutachter24', // Zukünftige DB
    userTypes: ['Gutachter', 'Sachverständige'],
    available: false // Noch nicht verbunden
  }
};

// Betriebsarten Mapping
const BUSINESS_TYPES = {
  autoglas: { label: 'Autoglaswerkstatt', icon: Square, color: 'bg-blue-500', lightColor: 'bg-blue-100', textColor: 'text-blue-700' },
  karosserie: { label: 'Karosserie/Lack', icon: Paintbrush, color: 'bg-orange-500', lightColor: 'bg-orange-100', textColor: 'text-orange-700' },
  reparatur: { label: 'Reparaturwerkstatt', icon: Wrench, color: 'bg-slate-500', lightColor: 'bg-slate-100', textColor: 'text-slate-700' },
  kombi: { label: 'Kombi-Betrieb', icon: Star, color: 'bg-purple-500', lightColor: 'bg-purple-100', textColor: 'text-purple-700' },
  // Autogutachter24 Typen (für Zukunft)
  gutachter: { label: 'Kfz-Gutachter', icon: ClipboardCheck, color: 'bg-indigo-500', lightColor: 'bg-indigo-100', textColor: 'text-indigo-700' },
  sachverstaendiger: { label: 'Sachverständiger', icon: Briefcase, color: 'bg-teal-500', lightColor: 'bg-teal-100', textColor: 'text-teal-700' }
};

// Sidebar Navigation Items
const SIDEBAR_ITEMS = [
  { id: 'dashboard', label: 'Übersicht', icon: BarChart3 },
  { id: 'tenants', label: 'Mandanten', icon: Building2 },
  { id: 'users', label: 'Benutzer', icon: Users },
  { id: 'pending', label: 'Freischaltung', icon: UserCheck },
  { id: 'dat', label: 'DAT-Zugänge', icon: Key },
  { id: 'activity', label: 'Aktivität', icon: Activity },
  { id: 'logins', label: 'Login-Historie', icon: Clock },
  { id: 'audit', label: 'Audit-Log', icon: FileText },
  { id: 'system', label: 'System', icon: Database },
  { id: 'settings', label: 'Einstellungen', icon: Settings },
];

function SuperAdmin() {
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeProduct, setActiveProduct] = useState('all'); // Multi-Produkt State
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBusinessType, setFilterBusinessType] = useState('all');
  
  // Modal States
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showCasesModal, setShowCasesModal] = useState(false);
  
  // Form States
  const [credentialsForm, setCredentialsForm] = useState({
    customerNumber: '',
    userLogin: '',
    password: '',
    partnerNumber: '1348925',
    partnerSignature: 'F38B0A0E7731A2939CBAD5E4316FD16F93E46284A4F967CA37EC5A8FB7FA3CF0'
  });
  const [editForm, setEditForm] = useState({
    company_name: '',
    business_type: '',
    modules: [],
    is_active: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantCases, setTenantCases] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  
  // New states for extended functionality
  const [allUsers, setAllUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [tenantsRes, statsRes] = await Promise.all([
        fetch(`${getBackendUrl()}/api/super-admin/tenants`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${getBackendUrl()}/api/super-admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (tenantsRes.ok) {
        const tenantsData = await tenantsRes.json();
        setTenants(tenantsData);
      }
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      const activityRes = await fetch(`${getBackendUrl()}/api/super-admin/activity-log?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (activityRes.ok) {
        const activityData = await activityRes.json();
        setActivityLog(activityData);
      }
      
      // Load all users
      const usersRes = await fetch(`${getBackendUrl()}/api/super-admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setAllUsers(usersData);
      }
      
      // Load pending users
      const pendingRes = await fetch(`${getBackendUrl()}/api/super-admin/pending-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingUsers(pendingData);
      }
      
      // Load login history
      const loginRes = await fetch(`${getBackendUrl()}/api/super-admin/login-history?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (loginRes.ok) {
        const loginData = await loginRes.json();
        setLoginHistory(loginData);
      }
      
      // Load admin logs
      const auditRes = await fetch(`${getBackendUrl()}/api/super-admin/admin-logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (auditRes.ok) {
        const auditData = await auditRes.json();
        setAdminLogs(auditData);
      }
      
      // Load system health
      const healthRes = await fetch(`${getBackendUrl()}/api/super-admin/system-health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (healthRes.ok) {
        const healthData = await healthRes.json();
        setSystemHealth(healthData);
      }
      
      // Load active sessions
      const sessionsRes = await fetch(`${getBackendUrl()}/api/super-admin/active-sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setActiveSessions(sessionsData);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Filtered tenants
  const filteredTenants = useMemo(() => {
    return tenants.filter(t => {
      const matchesSearch = !searchTerm || 
        t.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.tenant_id?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && t.has_dat_access) ||
        (filterStatus === 'pending' && !t.has_dat_access);
      
      const matchesBusinessType = filterBusinessType === 'all' ||
        t.business_type === filterBusinessType;
      
      return matchesSearch && matchesStatus && matchesBusinessType;
    });
  }, [tenants, searchTerm, filterStatus, filterBusinessType]);

  // Handlers
  const handleSaveCredentials = async () => {
    if (!selectedTenant) return;
    setSavingCredentials(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/tenants/${selectedTenant.tenant_id}/dat-credentials`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(credentialsForm)
        }
      );
      
      if (response.ok) {
        toast.success('DAT-Zugangsdaten erfolgreich gespeichert!');
        setShowCredentialsModal(false);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving credentials:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleRevokeDatAccess = async (tenantId) => {
    if (!window.confirm('DAT-Zugang wirklich entziehen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/tenants/${tenantId}/dat-credentials`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('DAT-Zugang entzogen');
        loadData();
      }
    } catch (error) {
      toast.error('Fehler beim Entziehen des Zugangs');
    }
  };

  // New handler functions for extended functionality
  const handleActivateUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/users/${userId}/activate`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('✅ Benutzer erfolgreich freigeschaltet!');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Freischalten');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const handleDeactivateUser = async (userId, reason = '') => {
    if (!window.confirm('Benutzer wirklich sperren?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/users/${userId}/deactivate?reason=${encodeURIComponent(reason)}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('🚫 Benutzer gesperrt');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Sperren');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Passwort zurücksetzen? Das temporäre Passwort wird angezeigt.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/users/${userId}/reset-password`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`🔐 Temporäres Passwort: ${data.temporary_password}`, { duration: 10000 });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Zurücksetzen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/users/${userId}/role?role=${newRole}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success(`Rolle geändert zu: ${newRole}`);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Ändern der Rolle');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedTenant) return;
    setSavingEdit(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/tenants/${selectedTenant.tenant_id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editForm)
        }
      );
      
      if (response.ok) {
        toast.success('Mandant erfolgreich aktualisiert!');
        setShowEditModal(false);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/tenants/${selectedTenant.tenant_id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Mandant und alle Daten gelöscht');
        setShowDeleteModal(false);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const loadTenantUsers = async (tenant) => {
    setSelectedTenant(tenant);
    setShowUsersModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/tenants/${tenant.tenant_id}/users`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setTenantUsers(data);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    }
  };

  const loadTenantCases = async (tenant) => {
    setSelectedTenant(tenant);
    setShowCasesModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/tenants/${tenant.tenant_id}/cases`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setTenantCases(data);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Akten');
    }
  };

  const openCredentialsModal = (tenant) => {
    setSelectedTenant(tenant);
    setCredentialsForm({
      customerNumber: tenant.dat_credentials?.customerNumber || '',
      userLogin: tenant.dat_credentials?.userLogin || '',
      password: '',
      partnerNumber: '1348925',
      partnerSignature: 'F38B0A0E7731A2939CBAD5E4316FD16F93E46284A4F967CA37EC5A8FB7FA3CF0'
    });
    setShowCredentialsModal(true);
  };

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    setEditForm({
      company_name: tenant.company_name || '',
      business_type: tenant.business_type || 'autoglas',
      modules: tenant.modules || [],
      is_active: tenant.is_active !== false
    });
    setShowEditModal(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stat Card Component
  const StatCard = ({ icon: Icon, label, value, subtext, color = 'blue', onClick }) => (
    <Card 
      className={`hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Business Type Badge
  const BusinessTypeBadge = ({ type }) => {
    const bt = BUSINESS_TYPES[type] || { label: 'Unbekannt', lightColor: 'bg-gray-100', textColor: 'text-gray-700', icon: Building2 };
    const IconComp = bt.icon;
    return (
      <Badge variant="secondary" className={`${bt.lightColor} ${bt.textColor} gap-1`}>
        <IconComp className="w-3 h-3" />
        {bt.label}
      </Badge>
    );
  };

  // Module Badges
  const ModuleBadges = ({ modules = [] }) => {
    if (!modules || modules.length === 0) {
      return <span className="text-muted-foreground text-xs">Keine</span>;
    }
    return (
      <div className="flex gap-1 flex-wrap">
        {modules.map(mod => (
          <Badge key={mod} variant="outline" className="text-xs">
            {mod === 'dat_glass' ? 'Glas' : mod === 'dat_myclaim' ? 'MyClaim' : mod}
          </Badge>
        ))}
      </div>
    );
  };

  // DAT Status Badge
  const DatStatusBadge = ({ hasAccess }) => (
    <Badge variant={hasAccess ? "default" : "secondary"} className={hasAccess ? "bg-green-600" : ""}>
      {hasAccess ? (
        <>
          <CheckCircle className="w-3 h-3 mr-1" />
          Aktiv
        </>
      ) : (
        <>
          <AlertCircle className="w-3 h-3 mr-1" />
          Ausstehend
        </>
      )}
    </Badge>
  );

  // Render Content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'tenants':
        return renderTenants();
      case 'users':
        return renderUsers();
      case 'pending':
        return renderPendingUsers();
      case 'dat':
        return renderDat();
      case 'activity':
        return renderActivity();
      case 'logins':
        return renderLoginHistory();
      case 'audit':
        return renderAuditLog();
      case 'system':
        return renderSystemHealth();
      case 'settings':
        return renderSettings();
      default:
        return renderDashboard();
    }
  };

  // Dashboard Content
  const renderDashboard = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Übersicht</h1>
        <p className="text-muted-foreground">
          {activeProduct === 'all' 
            ? 'Systemweite Statistiken aller Produkte' 
            : `${PRODUCTS[activeProduct]?.name} - Statistiken und Metriken`}
        </p>
      </div>

      {/* Multi-Produkt Übersicht - nur bei "Alle Produkte" */}
      {activeProduct === 'all' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Easyclaim24 Card */}
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-slate-900">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Easyclaim24</h3>
                    <p className="text-sm text-muted-foreground">Werkstätten & Schadenmanagement</p>
                  </div>
                </div>
                <Badge className="bg-green-500">Aktiv</Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{stats?.total_tenants || 0}</p>
                  <p className="text-xs text-muted-foreground">Mandanten</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{stats?.total_users || 0}</p>
                  <p className="text-xs text-muted-foreground">Benutzer</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{stats?.total_cases || 0}</p>
                  <p className="text-xs text-muted-foreground">Akten</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-blue-300"
                onClick={() => setActiveProduct('easyclaim')}
              >
                Details anzeigen
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Autogutachter24 Card - Coming Soon */}
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white dark:from-purple-950 dark:to-slate-900 opacity-75">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                    <ClipboardCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Autogutachter24</h3>
                    <p className="text-sm text-muted-foreground">Gutachter & Sachverständige</p>
                  </div>
                </div>
                <Badge variant="outline" className="border-purple-400 text-purple-600">Bald verfügbar</Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-purple-400">-</p>
                  <p className="text-xs text-muted-foreground">Mandanten</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-purple-400">-</p>
                  <p className="text-xs text-muted-foreground">Gutachter</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-purple-400">-</p>
                  <p className="text-xs text-muted-foreground">Gutachten</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-purple-300"
                disabled
              >
                Noch nicht verbunden
                <Lock className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              icon={Building2} 
              label="Mandanten" 
              value={stats?.total_tenants || 0}
              subtext="Registrierte Firmen"
              color="blue"
              onClick={() => setActiveTab('tenants')}
            />
            <StatCard 
              icon={Key} 
              label="DAT aktiv" 
              value={stats?.active_dat || 0}
              subtext="Freigeschaltete Zugänge"
              color="green"
              onClick={() => setActiveTab('dat')}
            />
            <StatCard 
              icon={AlertTriangle} 
              label="Ohne DAT" 
              value={stats?.without_dat || 0}
              subtext="Warten auf Freischaltung"
              color="amber"
            />
            <StatCard 
              icon={FileText} 
              label="Akten gesamt" 
              value={stats?.total_cases || 0}
              subtext={`${stats?.active_cases || 0} aktiv`}
              color="purple"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard 
              icon={Users} 
              label="Benutzer" 
              value={stats?.total_users || 0}
              subtext="Alle Accounts"
              color="slate"
            />
            <StatCard 
              icon={TrendingUp} 
              label="Neue (7 Tage)" 
              value={stats?.recent_registrations || 0}
              subtext="Registrierungen"
              color="cyan"
            />
            <StatCard 
              icon={Calendar} 
              label="Heute" 
              value={stats?.today_cases || 0}
              subtext="Neue Akten"
              color="indigo"
            />
          </div>

          {/* Business Type Distribution */}
          {stats?.business_type_distribution && Object.keys(stats.business_type_distribution).length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Betriebsarten-Verteilung</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(stats.business_type_distribution).map(([type, count]) => {
                    const bt = BUSINESS_TYPES[type] || { label: type, color: 'bg-gray-500', icon: Building2 };
                    const IconComp = bt.icon;
                    return (
                      <div key={type} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                        <div className={`w-10 h-10 ${bt.color} rounded-lg flex items-center justify-center`}>
                          <IconComp className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold">{count}</p>
                          <p className="text-xs text-muted-foreground">{bt.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Tenants & Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg">Neueste Mandanten</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab('tenants')} className="gap-1">
                  Alle <ChevronRight className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {tenants.slice(0, 5).map(tenant => {
                    const bt = BUSINESS_TYPES[tenant.business_type] || BUSINESS_TYPES.autoglas;
                    const IconComp = bt.icon;
                    return (
                      <div key={tenant.tenant_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 ${bt.lightColor} rounded-lg flex items-center justify-center`}>
                            <IconComp className={`w-4 h-4 ${bt.textColor}`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tenant.company_name || 'Unbenannt'}</p>
                            <p className="text-xs text-muted-foreground">{tenant.email}</p>
                          </div>
                        </div>
                        <DatStatusBadge hasAccess={tenant.has_dat_access} />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg">Letzte Aktivitäten</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab('activity')} className="gap-1">
                  Alle <ChevronRight className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[260px]">
                  <div className="space-y-2">
                    {activityLog.slice(0, 8).map((activity, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{activity.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {activity.details?.customer && `${activity.details.customer} • `}
                            {formatDateTime(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {activityLog.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">Keine Aktivitäten</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );

  // Tenants Content
  const renderTenants = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Mandanten</h1>
        <p className="text-muted-foreground">Alle registrierten Unternehmen verwalten</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Mandanten suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="tenant-search-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="active">DAT aktiv</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterBusinessType} onValueChange={setFilterBusinessType}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Betriebsart" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Betriebsarten</SelectItem>
                {Object.entries(BUSINESS_TYPES).map(([key, bt]) => (
                  <SelectItem key={key} value={key}>{bt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mandant</TableHead>
                <TableHead>Betriebsart</TableHead>
                <TableHead>Module</TableHead>
                <TableHead>DAT-Status</TableHead>
                <TableHead>Registriert</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
                    <p className="mt-2 text-muted-foreground">Lade Daten...</p>
                  </TableCell>
                </TableRow>
              ) : filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Building2 className="w-10 h-10 mx-auto text-muted-foreground/50" />
                    <p className="mt-2 text-muted-foreground">Keine Mandanten gefunden</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map(tenant => {
                  const bt = BUSINESS_TYPES[tenant.business_type] || BUSINESS_TYPES.autoglas;
                  const IconComp = bt.icon;
                  return (
                    <TableRow key={tenant.tenant_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${bt.lightColor} rounded-lg flex items-center justify-center`}>
                            <IconComp className={`w-5 h-5 ${bt.textColor}`} />
                          </div>
                          <div>
                            <p className="font-medium">{tenant.company_name || 'Unbenannt'}</p>
                            <p className="text-sm text-muted-foreground">{tenant.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><BusinessTypeBadge type={tenant.business_type} /></TableCell>
                      <TableCell><ModuleBadges modules={tenant.modules} /></TableCell>
                      <TableCell><DatStatusBadge hasAccess={tenant.has_dat_access} /></TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(tenant.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(tenant)}>
                              <Edit className="w-4 h-4 mr-2" />Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => loadTenantUsers(tenant)}>
                              <Users className="w-4 h-4 mr-2" />Benutzer
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => loadTenantCases(tenant)}>
                              <FileText className="w-4 h-4 mr-2" />Akten
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {tenant.has_dat_access ? (
                              <>
                                <DropdownMenuItem onClick={() => openCredentialsModal(tenant)}>
                                  <Key className="w-4 h-4 mr-2" />DAT bearbeiten
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRevokeDatAccess(tenant.tenant_id)} className="text-amber-600">
                                  <Lock className="w-4 h-4 mr-2" />DAT entziehen
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <DropdownMenuItem onClick={() => openCredentialsModal(tenant)}>
                                <Unlock className="w-4 h-4 mr-2" />DAT freischalten
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { setSelectedTenant(tenant); setShowDeleteModal(true); }} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <div className="text-sm text-muted-foreground">{filteredTenants.length} von {tenants.length} Mandanten</div>
    </div>
  );

  // DAT Content
  const renderDat = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">DAT-Zugänge</h1>
        <p className="text-muted-foreground">Übersicht aller DAT-Kalkulationszugänge</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-green-50 dark:bg-green-950/20 border-green-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{tenants.filter(t => t.has_dat_access).length}</p>
              <p className="text-sm text-green-600">Aktive Zugänge</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 dark:bg-amber-950/20 border-amber-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{tenants.filter(t => !t.has_dat_access).length}</p>
              <p className="text-sm text-amber-600">Ausstehend</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{tenants.length}</p>
              <p className="text-sm text-blue-600">Gesamt</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="DAT-Zugang suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="active">DAT aktiv</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* DAT Access Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mandant</TableHead>
                <TableHead>Betriebsart</TableHead>
                <TableHead>DAT-Kundennummer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Registriert</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
                    <p className="mt-2 text-muted-foreground">Lade Daten...</p>
                  </TableCell>
                </TableRow>
              ) : filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Key className="w-10 h-10 mx-auto text-muted-foreground/50" />
                    <p className="mt-2 text-muted-foreground">Keine DAT-Zugänge gefunden</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map(tenant => {
                  const bt = BUSINESS_TYPES[tenant.business_type] || BUSINESS_TYPES.autoglas;
                  const IconComp = bt.icon;
                  return (
                    <TableRow key={tenant.tenant_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${bt.lightColor} rounded-lg flex items-center justify-center`}>
                            <IconComp className={`w-5 h-5 ${bt.textColor}`} />
                          </div>
                          <div>
                            <p className="font-medium">{tenant.company_name || 'Unbenannt'}</p>
                            <p className="text-sm text-muted-foreground">{tenant.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><BusinessTypeBadge type={tenant.business_type} /></TableCell>
                      <TableCell>
                        {tenant.has_dat_access ? (
                          <span className="font-mono text-sm">{tenant.dat_credentials?.customerNumber || '-'}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell><DatStatusBadge hasAccess={tenant.has_dat_access} /></TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(tenant.created_at)}</TableCell>
                      <TableCell className="text-right">
                        {tenant.has_dat_access ? (
                          <div className="flex justify-end gap-1">
                            <Button variant="outline" size="sm" onClick={() => openCredentialsModal(tenant)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" className="text-amber-600 hover:text-amber-700" onClick={() => handleRevokeDatAccess(tenant.tenant_id)}>
                              <Lock className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" onClick={() => openCredentialsModal(tenant)}>
                            <Unlock className="w-4 h-4 mr-1" />
                            Freischalten
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  // Activity Content
  const renderActivity = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Aktivitätsprotokoll</h1>
        <p className="text-muted-foreground">Letzte Systemaktivitäten</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <ScrollArea className="h-[600px]">
            <div className="space-y-4">
              {activityLog.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{activity.description}</p>
                    <div className="flex flex-wrap gap-2 mt-1 text-sm text-muted-foreground">
                      {activity.details?.customer && (
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{activity.details.customer}</span>
                      )}
                      {activity.details?.vehicle && (
                        <span className="flex items-center gap-1"><Box className="w-3 h-3" />{activity.details.vehicle}</span>
                      )}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDateTime(activity.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {activityLog.length === 0 && (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">Keine Aktivitäten vorhanden</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );

  // Users Content - Alle Benutzer
  const renderUsers = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Alle Benutzer</h1>
        <p className="text-muted-foreground">Übersicht aller registrierten Benutzer</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Firma</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allUsers.map((user) => (
                <TableRow key={user._id}>
                  <TableCell className="font-medium">{user.name || '-'}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.company_name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{user.role || 'user'}</Badge>
                  </TableCell>
                  <TableCell>
                    {user.is_active !== false ? (
                      <Badge className="bg-green-500">Aktiv</Badge>
                    ) : (
                      <Badge variant="destructive">Gesperrt</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {user.is_active !== false ? (
                          <DropdownMenuItem onClick={() => handleDeactivateUser(user._id)}>
                            <UserX className="w-4 h-4 mr-2" />
                            Sperren
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleActivateUser(user._id)}>
                            <UserCheck className="w-4 h-4 mr-2" />
                            Freischalten
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleResetPassword(user._id)}>
                          <Key className="w-4 h-4 mr-2" />
                          Passwort zurücksetzen
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleChangeRole(user._id, 'admin')}>
                          <Shield className="w-4 h-4 mr-2" />
                          Zum Admin machen
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleChangeRole(user._id, 'user')}>
                          <User className="w-4 h-4 mr-2" />
                          Zum Benutzer machen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  // Pending Users Content - Wartende Benutzer
  const renderPendingUsers = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Benutzer-Freischaltung</h1>
        <p className="text-muted-foreground">Benutzer warten auf Ihre Freigabe</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-amber-50 dark:bg-amber-950/20 border-amber-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{pendingUsers.length}</p>
              <p className="text-sm text-amber-600">Wartend</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-950/20 border-green-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{allUsers.filter(u => u.is_active !== false).length}</p>
              <p className="text-sm text-green-600">Aktiv</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{allUsers.length}</p>
              <p className="text-sm text-blue-600">Gesamt</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Benutzer</TableHead>
                <TableHead>Firma</TableHead>
                <TableHead>Betriebsart</TableHead>
                <TableHead>Dokumente</TableHead>
                <TableHead>Registriert</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <UserCheck className="w-12 h-12 mx-auto text-green-500 mb-4" />
                    <h3 className="font-semibold text-lg">Keine wartenden Benutzer</h3>
                    <p className="text-muted-foreground">Alle Benutzer wurden bereits freigeschaltet.</p>
                  </TableCell>
                </TableRow>
              ) : (
                pendingUsers.map((user) => {
                  const bt = BUSINESS_TYPES[user.business_type] || BUSINESS_TYPES.autoglas;
                  const IconComp = bt.icon;
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${bt.lightColor} rounded-lg flex items-center justify-center`}>
                            <IconComp className={`w-5 h-5 ${bt.textColor}`} />
                          </div>
                          <div>
                            <p className="font-medium">{user.name || 'Unbenannt'}</p>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{user.company_name || '-'}</TableCell>
                      <TableCell><BusinessTypeBadge type={user.business_type} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {user.has_qualification_proof && (
                            <Badge className="bg-green-500 text-xs">Qualifikation</Badge>
                          )}
                          {user.has_business_registration && (
                            <Badge className="bg-blue-500 text-xs">Gewerbe</Badge>
                          )}
                          {!user.has_qualification_proof && !user.has_business_registration && (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(user.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeactivateUser(user.id, 'Anfrage abgelehnt')}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => handleActivateUser(user.id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Freischalten
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  // Login History Content
  const renderLoginHistory = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Login-Historie</h1>
        <p className="text-muted-foreground">Übersicht aller Login-Vorgänge</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loginHistory.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Keine Login-Daten vorhanden</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeitpunkt</TableHead>
                  <TableHead>Benutzer</TableHead>
                  <TableHead>Mandant</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loginHistory.map((log, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {new Date(log.timestamp).toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell>{log.email}</TableCell>
                    <TableCell className="font-mono text-xs">{log.tenant_id?.slice(0, 8)}...</TableCell>
                    <TableCell>
                      {log.success ? (
                        <Badge className="bg-green-500">Erfolgreich</Badge>
                      ) : (
                        <Badge variant="destructive">Fehlgeschlagen</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Aktive Sessions ({activeSessions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeSessions.length === 0 ? (
            <p className="text-muted-foreground">Keine aktiven Sessions</p>
          ) : (
            <div className="space-y-2">
              {activeSessions.map((session, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">{session.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Letzte Aktivität: {new Date(session.last_activity).toLocaleString('de-DE')}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-green-100 text-green-800">Online</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Audit Log Content
  const renderAuditLog = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Audit-Log</h1>
        <p className="text-muted-foreground">Protokoll aller Admin-Aktionen</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {adminLogs.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Noch keine Admin-Aktionen protokolliert</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeitpunkt</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adminLogs.map((log, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {new Date(log.timestamp).toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell>{log.admin_email || log.admin_name || 'System'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {log.action === 'user_activated' && '✅ Benutzer freigeschaltet'}
                        {log.action === 'user_deactivated' && '🚫 Benutzer gesperrt'}
                        {log.action === 'password_reset' && '🔐 Passwort zurückgesetzt'}
                        {log.action === 'role_changed' && '👤 Rolle geändert'}
                        {!['user_activated', 'user_deactivated', 'password_reset', 'role_changed'].includes(log.action) && log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {log.details ? JSON.stringify(log.details) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // System Health Content
  const [clearDbDialogOpen, setClearDbDialogOpen] = useState(false);
  const [clearDbPassword, setClearDbPassword] = useState('');
  const [clearingDb, setClearingDb] = useState(false);

  const handleClearDatabase = async () => {
    if (!clearDbPassword) {
      toast.error('Bitte Passwort eingeben');
      return;
    }
    
    setClearingDb(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${getBackendUrl()}/api/super-admin/clear-database?confirm_password=${encodeURIComponent(clearDbPassword)}&keep_super_admin=true`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`✅ Datenbank geleert! ${data.total_deleted} Einträge gelöscht.`);
        setClearDbDialogOpen(false);
        setClearDbPassword('');
        loadData(); // Reload stats
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Leeren der Datenbank');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    } finally {
      setClearingDb(false);
    }
  };

  const renderSystemHealth = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">System-Status</h1>
        <p className="text-muted-foreground">Überwachung der Systemgesundheit</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              systemHealth?.status === 'healthy' ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {systemHealth?.status === 'healthy' ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h3 className="font-semibold mt-3">System-Status</h3>
            <p className={`text-lg ${systemHealth?.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
              {systemHealth?.status === 'healthy' ? 'Gesund' : 'Problem'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <Database className="w-16 h-16 mx-auto text-blue-500" />
            <h3 className="font-semibold mt-3">Datenbank</h3>
            <p className="text-lg text-green-600">
              {systemHealth?.database?.connected ? 'Verbunden' : 'Getrennt'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <Activity className="w-16 h-16 mx-auto text-purple-500" />
            <h3 className="font-semibold mt-3">Aktive Nutzer (24h)</h3>
            <p className="text-3xl font-bold">{systemHealth?.database?.active_users_24h || 0}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Statistiken</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold">{systemHealth?.database?.users_total || 0}</p>
              <p className="text-sm text-muted-foreground">Benutzer gesamt</p>
            </div>
            <div className="p-4 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold">{systemHealth?.database?.cases_total || 0}</p>
              <p className="text-sm text-muted-foreground">Akten gesamt</p>
            </div>
            <div className="p-4 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold">{systemHealth?.errors_24h || 0}</p>
              <p className="text-sm text-muted-foreground">Fehler (24h)</p>
            </div>
            <div className="p-4 bg-muted rounded-lg text-center">
              <p className="text-2xl font-bold">{activeSessions.length}</p>
              <p className="text-sm text-muted-foreground">Aktive Sessions</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Letzte Aktualisierung: {systemHealth?.timestamp ? new Date(systemHealth.timestamp).toLocaleString('de-DE') : '-'}
          </p>
          <Button variant="outline" className="mt-2" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
        <CardHeader>
          <CardTitle className="text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Gefahrenzone
          </CardTitle>
          <CardDescription className="text-red-500">
            Diese Aktionen können nicht rückgängig gemacht werden!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-lg border border-red-200">
            <div>
              <h4 className="font-semibold">Datenbank leeren</h4>
              <p className="text-sm text-muted-foreground">
                Löscht alle Mandanten, Akten, Kunden und Einstellungen. Super Admin bleibt erhalten.
              </p>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setClearDbDialogOpen(true)}
              data-testid="clear-database-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Datenbank leeren
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clear Database Dialog */}
      <Dialog open={clearDbDialogOpen} onOpenChange={setClearDbDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Datenbank leeren
            </DialogTitle>
            <DialogDescription>
              Diese Aktion löscht ALLE Daten unwiderruflich:
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                Alle Mandanten und Benutzer
              </li>
              <li className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                Alle Akten und Kunden
              </li>
              <li className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                Alle Einstellungen und DAT-Zugänge
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Super Admin bleibt erhalten
              </li>
            </ul>
            
            <div className="mt-4">
              <label className="text-sm font-medium">
                Super Admin Passwort zur Bestätigung:
              </label>
              <Input
                type="password"
                value={clearDbPassword}
                onChange={(e) => setClearDbPassword(e.target.value)}
                placeholder="Passwort eingeben"
                className="mt-2"
                data-testid="clear-db-password"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearDbDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearDatabase}
              disabled={clearingDb || !clearDbPassword}
              data-testid="confirm-clear-db"
            >
              {clearingDb ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Wird gelöscht...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Endgültig löschen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Settings Content
  const renderSettings = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Einstellungen</h1>
        <p className="text-muted-foreground">Globale Konfiguration der Easyclaim24 Software</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Key className="w-5 h-5" />DAT Partner-Zugangsdaten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <Label className="text-muted-foreground">Partner-Nummer</Label>
              <Input value="1348925" readOnly className="mt-1 bg-background" />
            </div>
            <div>
              <Label className="text-muted-foreground">Partner-Signatur</Label>
              <Input type="password" value="F38B0A0E7731A2939CBAD5E4316FD16F93E46284A4F967CA37EC5A8FB7FA3CF0" readOnly className="mt-1 bg-background" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h3 className="font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-2 mb-2">
              <Info className="w-4 h-4" />Hinweise
            </h3>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• Die Partner-Zugangsdaten werden für alle DAT-Anfragen verwendet</li>
              <li>• Jeder Mandant benötigt eigene DAT-Kundenzugangsdaten</li>
              <li>• Die Zugangsdaten werden verschlüsselt gespeichert</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" />Super Admin Zugangsdaten</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-100 dark:bg-slate-900 rounded-lg">
            <div>
              <Label className="text-muted-foreground">E-Mail</Label>
              <p className="font-mono mt-1">superadmin@intelilack.de</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Passwort</Label>
              <p className="font-mono mt-1">SuperAdmin2024!</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex" data-testid="super-admin-page">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-slate-900 text-white flex flex-col transition-all duration-300 fixed h-full z-40`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6" />
              <span className="font-bold">Super Admin</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-800"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Produkt-Switcher */}
        {sidebarOpen && (
          <div className="p-3 border-b border-slate-700">
            <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider">Produkt</p>
            <div className="space-y-1">
              {Object.values(PRODUCTS).map(product => {
                const ProductIcon = product.icon;
                const isActive = activeProduct === product.id;
                return (
                  <button
                    key={product.id}
                    onClick={() => product.available && setActiveProduct(product.id)}
                    disabled={!product.available}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive 
                        ? `${product.color} text-white` 
                        : product.available 
                          ? 'text-slate-300 hover:bg-slate-800'
                          : 'text-slate-500 cursor-not-allowed opacity-50'
                    }`}
                    data-testid={`product-${product.id}`}
                  >
                    <ProductIcon className="w-4 h-4 flex-shrink-0" />
                    <span>{product.shortName}</span>
                    {!product.available && (
                      <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1 border-slate-600">
                        Bald
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-2 overflow-y-auto">
          {SIDEBAR_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-colors ${
                  isActive 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`sidebar-${item.id}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-2 border-t border-slate-700">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Dashboard</span>}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-red-400 hover:bg-red-900/30 transition-colors"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Abmelden</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} transition-all duration-300`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-background border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-muted-foreground">
              {PRODUCTS[activeProduct]?.name || 'Super Admin'} - Control Center
            </h2>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Refresh Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Daten aktualisieren</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {/* User Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2 px-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    SA
                  </div>
                  <div className="text-left hidden md:block">
                    <p className="text-sm font-medium">Super Admin</p>
                    <p className="text-xs text-muted-foreground">superadmin@intelilack.de</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {/* Header */}
                <div className="px-3 py-3 border-b">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                      SA
                    </div>
                    <div>
                      <p className="font-semibold">Super Admin</p>
                      <p className="text-xs text-muted-foreground">superadmin@intelilack.de</p>
                    </div>
                  </div>
                </div>
                
                {/* Menu Items */}
                <DropdownMenuItem onClick={() => setActiveTab('settings')}>
                  <Building2 className="w-4 h-4 mr-3" />
                  Unternehmen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveTab('settings')}>
                  <Settings className="w-4 h-4 mr-3" />
                  Einstellungen
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <BookOpen className="w-4 h-4 mr-3" />
                  Schulung
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Headphones className="w-4 h-4 mr-3" />
                  Support
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <MessageSquare className="w-4 h-4 mr-3" />
                  Chat
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Lightbulb className="w-4 h-4 mr-3" />
                  Verbesserung
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem>
                  <Info className="w-4 h-4 mr-3" />
                  Impressum
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Lock className="w-4 h-4 mr-3" />
                  Datenschutz
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Accessibility className="w-4 h-4 mr-3" />
                  Barrierefreiheit
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                  <LogOut className="w-4 h-4 mr-3" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </main>

      {/* Modals */}
      {/* Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>DAT-Zugangsdaten</DialogTitle>
            <DialogDescription>{selectedTenant?.company_name || selectedTenant?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>DAT Kundennummer *</Label>
              <Input value={credentialsForm.customerNumber} onChange={(e) => setCredentialsForm({...credentialsForm, customerNumber: e.target.value})} placeholder="z.B. 1234567" />
            </div>
            <div className="space-y-2">
              <Label>DAT Benutzername *</Label>
              <Input value={credentialsForm.userLogin} onChange={(e) => setCredentialsForm({...credentialsForm, userLogin: e.target.value})} placeholder="Benutzername" />
            </div>
            <div className="space-y-2">
              <Label>DAT Passwort *</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={credentialsForm.password} onChange={(e) => setCredentialsForm({...credentialsForm, password: e.target.value})} placeholder="••••••••" className="pr-10" />
                <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            {selectedTenant?.has_dat_access && (
              <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />Lassen Sie das Passwort leer, um es nicht zu ändern.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsModal(false)}>Abbrechen</Button>
            <Button onClick={handleSaveCredentials} disabled={savingCredentials}>
              {savingCredentials ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Mandant bearbeiten</DialogTitle>
            <DialogDescription>{selectedTenant?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Firmenname</Label>
              <Input value={editForm.company_name} onChange={(e) => setEditForm({...editForm, company_name: e.target.value})} placeholder="Firmenname" />
            </div>
            <div className="space-y-2">
              <Label>Betriebsart</Label>
              <Select value={editForm.business_type} onValueChange={(value) => setEditForm({...editForm, business_type: value})}>
                <SelectTrigger><SelectValue placeholder="Betriebsart wählen" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(BUSINESS_TYPES).map(([key, bt]) => (
                    <SelectItem key={key} value={key}>{bt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Module</Label>
              <div className="space-y-2 p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-2">
                  <Checkbox id="module_glass" checked={editForm.modules.includes('dat_glass')} onCheckedChange={(checked) => {
                    if (checked) setEditForm({...editForm, modules: [...editForm.modules, 'dat_glass']});
                    else setEditForm({...editForm, modules: editForm.modules.filter(m => m !== 'dat_glass')});
                  }} />
                  <label htmlFor="module_glass" className="text-sm">DAT Glas</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="module_myclaim" checked={editForm.modules.includes('dat_myclaim')} onCheckedChange={(checked) => {
                    if (checked) setEditForm({...editForm, modules: [...editForm.modules, 'dat_myclaim']});
                    else setEditForm({...editForm, modules: editForm.modules.filter(m => m !== 'dat_myclaim')});
                  }} />
                  <label htmlFor="module_myclaim" className="text-sm">DAT MyClaim</label>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="is_active" checked={editForm.is_active} onCheckedChange={(checked) => setEditForm({...editForm, is_active: checked})} />
              <label htmlFor="is_active" className="text-sm">Account aktiv</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Abbrechen</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />Mandant löschen
            </DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie <strong>{selectedTenant?.company_name || selectedTenant?.email}</strong> und alle Daten löschen möchten?
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive"><strong>Warnung:</strong> Diese Aktion löscht alle Benutzer, Akten, Kunden und Einstellungen.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleDeleteTenant}><Trash2 className="w-4 h-4 mr-2" />Endgültig löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Users Modal */}
      <Dialog open={showUsersModal} onOpenChange={setShowUsersModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Benutzer von {selectedTenant?.company_name || 'Mandant'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenantUsers.map(user => (
                  <TableRow key={user._id}>
                    <TableCell className="font-medium">{user.full_name || '-'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell><Badge variant="outline">{user.role}</Badge></TableCell>
                    <TableCell>{user.is_active !== false ? <Badge className="bg-green-600">Aktiv</Badge> : <Badge variant="secondary">Inaktiv</Badge>}</TableCell>
                  </TableRow>
                ))}
                {tenantUsers.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Keine Benutzer</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
          <DialogFooter><Button variant="outline" onClick={() => setShowUsersModal(false)}>Schließen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cases Modal */}
      <Dialog open={showCasesModal} onOpenChange={setShowCasesModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Akten von {selectedTenant?.company_name || 'Mandant'}</DialogTitle>
          </DialogHeader>
          {tenantCases && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold">{tenantCases.total}</p>
                  <p className="text-sm text-muted-foreground">Gesamt</p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{tenantCases.active}</p>
                  <p className="text-sm text-muted-foreground">Aktiv</p>
                </div>
                <div className="p-4 bg-slate-100 dark:bg-slate-900 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-600">{tenantCases.archived}</p>
                  <p className="text-sm text-muted-foreground">Archiviert</p>
                </div>
              </div>
              {tenantCases.recent?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Letzte Akten</h4>
                  <ScrollArea className="max-h-[200px]">
                    <div className="space-y-2">
                      {tenantCases.recent.map(c => (
                        <div key={c._id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium">{c.case_number || 'Ohne Nummer'}</p>
                            <p className="text-sm text-muted-foreground">{c.customer_name || '-'}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline">{c.status || 'Offen'}</Badge>
                            <p className="text-xs text-muted-foreground mt-1">{formatDate(c.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowCasesModal(false)}>Schließen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SuperAdmin;
